import { getDecodedShopifyProductIdString } from "@utils/index"
import React from "react"

const GTM_EE_EVENT = "gtm-ee-event"
const GTM_EE_EVENT_CATEGORY = "gtm-ee-event-category"
const GTM_EE_EVENT_CATEGORY_ENHANCED_COMMERCE = "EnhancedEcommerce"
const GTM_EE_EVENT_ACTION = "gtm-ee-event-action"
const GTM_EE_EVENT_ACTION_VIEW_PRODUCT_LIST = "ProductImpressions"
const GTM_EE_EVENT_ACTION_CLICK_PRODUCT_CARD = "ProductClicks"
const GTM_EE_EVENT_ACTION_VIEW_PRODUCT_DETAILS = "ProductDetails"
const GTM_EE_EVENT_ACTION_ADD_PRODUCT_TO_CART = "AddingaProducttoaShoppingCart"
const GTM_EE_EVENT_ACTION_REMOVE_PRODUCT_FROM_CART =
  "RemovingaProductfromaShoppingCart"
const GTM_EE_EVENT_NON_INTERACTION = "gtm-ee-event-non-interaction"
const GTM_EE_EVENT_NON_INTERACTION_TRUE = "True"
const GTM_EE_EVENT_NON_INTERACTION_FALSE = "False"

export interface GTMProductProps {
  brand: string
  device: string
  deviceType: string
  list?: string
  price: string
  productId: string
  productName: string
  quantity?: number
  sku: string
  variantName: string
}

const formatProduct = ({
  brand,
  device,
  deviceType,
  list,
  price,
  productId,
  productName,
  quantity,
  sku,
  variantName,
}: GTMProductProps) => {
  const product: {
    brand: string
    category: string
    id: string
    list?: string
    name: string
    price: string
    quantity?: number
    sku: string
    variant: string
  } = {
    brand: brand,
    category: `${deviceType} / ${device}`,
    id: getDecodedShopifyProductIdString(productId),
    name: productName,
    price: price,
    sku,
    variant: variantName,
  }

  if (quantity !== undefined) {
    product.quantity = quantity
  }

  if (list) {
    product.list = list
  }

  return product
}

const getDataLayer = () => {
  const _window = typeof window !== "undefined" && (window as any)
  if (_window) {
    return _window.dataLayer
  }
}

// Stable empty array reference
const EMPTY_DATALAYER: any[] = []

export default () => {
  const dataLayer = React.useMemo(() => {
    return getDataLayer() || EMPTY_DATALAYER
  }, [typeof window !== "undefined" && (window as any)?.dataLayer])

  // Required: list
  const onViewProductList = React.useCallback(
    (products: Array<GTMProductProps>) => {
      if (dataLayer) {
        dataLayer.push({
          ecommerce: {
            currencyCode: "USD",
            impressions: products.map(formatProduct),
          },
          event: GTM_EE_EVENT,
          [GTM_EE_EVENT_ACTION]: GTM_EE_EVENT_ACTION_VIEW_PRODUCT_LIST,
          [GTM_EE_EVENT_CATEGORY]: GTM_EE_EVENT_CATEGORY_ENHANCED_COMMERCE,
          [GTM_EE_EVENT_NON_INTERACTION]: GTM_EE_EVENT_NON_INTERACTION_TRUE,
        })
      }
    },
    [dataLayer]
  )

  // Required: list
  const onClickProductCard = React.useCallback(
    (product: GTMProductProps) => {
      if (dataLayer) {
        dataLayer.push({
          ecommerce: {
            click: {
              actionField: { list: product.list || "" },
              products: [formatProduct(product)],
            },
            currencyCode: "USD",
          },
          event: GTM_EE_EVENT,
          [GTM_EE_EVENT_ACTION]: GTM_EE_EVENT_ACTION_CLICK_PRODUCT_CARD,
          [GTM_EE_EVENT_CATEGORY]: GTM_EE_EVENT_CATEGORY_ENHANCED_COMMERCE,
          [GTM_EE_EVENT_NON_INTERACTION]: GTM_EE_EVENT_NON_INTERACTION_FALSE,
        })
      }
    },
    [dataLayer]
  )

  // Required: list === "Product Details"
  const onViewProductDetails = React.useCallback(
    (product: GTMProductProps) => {
      if (dataLayer) {
        dataLayer.push({
          ecommerce: {
            currencyCode: "USD",
            detail: {
              actionField: { list: product.list },
              products: [formatProduct(product)],
            },
            impressions: [formatProduct(product)],
          },
          event: GTM_EE_EVENT,
          [GTM_EE_EVENT_ACTION]: GTM_EE_EVENT_ACTION_VIEW_PRODUCT_DETAILS,
          [GTM_EE_EVENT_CATEGORY]: GTM_EE_EVENT_CATEGORY_ENHANCED_COMMERCE,
          [GTM_EE_EVENT_NON_INTERACTION]: GTM_EE_EVENT_NON_INTERACTION_TRUE,
        })
      }
    },
    [dataLayer]
  )

  // Required: quantity
  const onAddProductToCart = React.useCallback(
    (product: GTMProductProps) => {
      if (dataLayer) {
        dataLayer.push({
          ecommerce: {
            add: {
              products: [formatProduct(product)],
            },
            currencyCode: "USD",
          },
          event: GTM_EE_EVENT,
          [GTM_EE_EVENT_ACTION]: GTM_EE_EVENT_ACTION_ADD_PRODUCT_TO_CART,
          [GTM_EE_EVENT_CATEGORY]: GTM_EE_EVENT_CATEGORY_ENHANCED_COMMERCE,
          [GTM_EE_EVENT_NON_INTERACTION]: GTM_EE_EVENT_NON_INTERACTION_FALSE,
        })
      }
    },
    [dataLayer]
  )

  // Required: quantity
  const onRemoveProductFromCart = React.useCallback(
    (product: GTMProductProps) => {
      if (dataLayer) {
        dataLayer.push({
          ecommerce: {
            currencyCode: "USD",
            remove: {
              products: [formatProduct(product)],
            },
          },
          event: GTM_EE_EVENT,
          [GTM_EE_EVENT_ACTION]: GTM_EE_EVENT_ACTION_REMOVE_PRODUCT_FROM_CART,
          [GTM_EE_EVENT_CATEGORY]: GTM_EE_EVENT_CATEGORY_ENHANCED_COMMERCE,
          [GTM_EE_EVENT_NON_INTERACTION]: GTM_EE_EVENT_NON_INTERACTION_FALSE,
        })
      }
    },
    [dataLayer]
  )

  return {
    onAddProductToCart,
    onClickProductCard,
    onRemoveProductFromCart,
    onViewProductDetails,
    onViewProductList,
  }
}
